/**
 * OaSIS Explorer Frontend - QueryLoadingState Component
 * Task 8.2: Component Abstraction & Generalization
 * 
 * Reusable loading state wrapper for tRPC queries
 * Eliminates duplicate loading logic across 7+ pages
 */

import React from 'react';
import { Card } from './Card';
import { Loading } from './Loading';

/**
 * Props for QueryLoadingState component
 */
interface QueryLoadingStateProps {
  /** tRPC query object with isLoading state */
  query: { isLoading: boolean; error?: unknown; data?: unknown };
  /** Custom loading text to display */
  loadingText?: string;
  /** Wrapper mode for the loading component */
  wrapper?: 'card' | 'page' | 'none';
  /** Content to render when not loading */
  children: React.ReactNode;
}

/**
 * QueryLoadingState Component
 * 
 * Wraps content and shows loading state while query is loading.
 * Supports three wrapper modes:
 * - 'card': Wraps loading in Card component (default)
 * - 'page': Wraps loading in page container
 * - 'none': No wrapper, just loading component
 * 
 * @example
 * ```tsx
 * <QueryLoadingState 
 *   query={occupationsQuery} 
 *   loadingText="Loading occupations..."
 *   wrapper="card"
 * >
 *   <div>{occupationsQuery.data}</div>
 * </QueryLoadingState>
 * ```
 */
export const QueryLoadingState: React.FC<QueryLoadingStateProps> = ({
  query,
  loadingText = 'Loading...',
  wrapper = 'card',
  children,
}) => {
  if (query.isLoading) {
    const loadingContent = <Loading text={loadingText} />;
    
    if (wrapper === 'card') {
      return <Card>{loadingContent}</Card>;
    }
    if (wrapper === 'page') {
      return <div className="container mx-auto px-4 py-8">{loadingContent}</div>;
    }
    return loadingContent;
  }

  return <>{children}</>;
};

